import { useEffect, useMemo, useState, useCallback } from "react";
import { Image, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import AppPage from "layouts/AppPage";
import { Heading18 } from "components/Heading";
import { useNavbar } from "hooks/useNavbar";
import EmptyState from "components/EmptyComponents/EmptyState";
import CoworkersSVG from "assets/undraw_co_workers.svg";
import NewHireJourneysList from "features/NewHireJourney/Components/NewHireJourneysList";
import NewHireJourneysFilters, {
  NewHireJourneysFilters as FiltersType,
} from "features/NewHireJourney/Components/NewHireJourneysFilters";

const defaultFilters: FiltersType = {};

const sortOptions = [
  { label: "Start Date: Newest First", value: "start_date_desc" },
  { label: "Start Date: Oldest First", value: "start_date_asc" },
  { label: "Name: A-Z", value: "name_asc" },
  { label: "Name: Z-A", value: "name_desc" },
];

const defaultSortString = "start_date_desc";

export default function MyJourneysPage() {
  const { setPageTitle } = useNavbar();
  const [filters, setFilters] = useState<FiltersType>(defaultFilters);
  const [sort, setSort] = useState<string>(defaultSortString);

  useEffect(() => {
    setPageTitle("Supported Journeys");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  const handleFiltersChange = useCallback((newFilters: FiltersType) => {
    setFilters(newFilters);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSort(value || defaultSortString);
  }, []);

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string | string[]> = { mine: "true" };

    if (filters.q && filters.q.length > 2) queries.q = filters.q;
    if (filters.needs_attention === "true") queries.needs_attention = "true";
    if (filters.with_open_tasks === "true") queries.with_open_tasks = "true";
    if (filters.with_overdue_tasks === "true") queries.with_overdue_tasks = "true";
    if (filters.stage) queries.stage = filters.stage;

    if (sort) {
      const sep = sort.lastIndexOf("_");
      queries["sort[0][sort_by]"] = sort.slice(0, sep);
      queries["sort[0][direction]"] = sort.slice(sep + 1);
    }

    return queries;
  }, [filters, sort]);

  const emptyState = (
    <EmptyState message="You are not involved in any journeys">
      <Image maxWidth="240px" maxHeight="240px" src={CoworkersSVG} />
    </EmptyState>
  );

  return (
    <AppPage>
      <VStack spacing={6} align="stretch">
        <VStack mt="4" spacing={1} align="flex-start">
          <Heading18>Supported Journeys</Heading18>
          <CDS.Text12 color="fg.subtle">Journeys that you are involved in</CDS.Text12>
        </VStack>

        <NewHireJourneysFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleReset}
          currentSort={sort}
          defaultSort={defaultSortString}
          onSortChange={handleSortChange}
          sortOptions={sortOptions}
        />
        <NewHireJourneysList
          additionalQueries={additionalQueries}
          emptyStateComponent={emptyState}
        />
      </VStack>
    </AppPage>
  );
}
